"use client";

/**
 * Responsive shell for the read-only source mode of ``SingleFileView``.
 *
 * Agent-chat links have a signed read URL but no configured view reference.
 * Keep that capability boundary intact: the embedded surface can fetch and
 * download the file, but it never receives an edit URL.
 */

import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { SingleFileView } from "@/components/views/single-file-view";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import type { AsidePanelResize } from "@/hooks/use-aside-view";
import { useIsMobile } from "@/hooks/use-media-query";

export interface SourceFileViewerPanelProps {
  /** Signed read-only ``/files/d/{jwt}`` URL. */
  url: string;
  /** Filename decoded from signed metadata or the safe link fallback. */
  fileName: string;
  onClose: () => void;
  onOpenInNewWindow?: () => void;
  resize: AsidePanelResize;
}

export function SourceFileViewerPanel({
  url,
  fileName,
  onClose,
  onOpenInNewWindow,
  resize,
}: SourceFileViewerPanelProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const content = (
    <SingleFileView
      sourceUrl={url}
      sourceFileName={fileName}
      onClose={onClose}
      onOpenInNewTab={onOpenInNewWindow}
      className="h-full"
    />
  );

  if (isMobile) {
    return (
      <Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent
          className="flex h-full max-h-full w-full max-w-full flex-col gap-0 overflow-hidden rounded-none p-0"
          hideCloseButton
          data-testid="source-file-viewer-dialog"
        >
          <DialogTitle className="sr-only">{fileName}</DialogTitle>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <AnimatePresence>
      <motion.aside
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className="relative flex h-full min-w-0 shrink-0 flex-col overflow-hidden border-l bg-background"
        style={{ width: resize.width }}
        data-testid="source-file-viewer-panel"
      >
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t("viewer.resizeSource")}
          onPointerDown={resize.onPointerDown}
          onPointerMove={resize.onPointerMove}
          onPointerUp={resize.onPointerUp}
          onDoubleClick={resize.onDoubleClick}
          className="absolute -left-[3px] top-0 z-20 h-full w-1.5 cursor-col-resize touch-none select-none transition-colors hover:bg-primary/30 active:bg-primary/40"
        />
        {content}
      </motion.aside>
    </AnimatePresence>
  );
}
